import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Shield, User, FolderKanban } from 'lucide-react';

export const UserManagement = () => {
  const { get, post, put, del } = useApi();
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUserForCampaigns, setSelectedUserForCampaigns] = useState(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'call_agent'
  });

  const loadData = useCallback(async () => {
    try {
      const [usersData, campaignsData] = await Promise.all([
        get('/users'),
        get('/campaigns')
      ]);
      setUsers(usersData);
      setCampaigns(campaignsData);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'call_agent'
    });
    setEditingUser(null);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role
    });
    setDialogOpen(true);
  };

  const openCampaignDialog = (user) => {
    setSelectedUserForCampaigns(user);
    setSelectedCampaigns(user.campaigns || []);
    setCampaignDialogOpen(true);
  };

  const handleCampaignToggle = (campaignId) => {
    setSelectedCampaigns(prev => {
      if (prev.includes(campaignId)) {
        return prev.filter(id => id !== campaignId);
      } else {
        return [...prev, campaignId];
      }
    });
  };

  const handleSaveCampaigns = async () => {
    try {
      await put(`/users/${selectedUserForCampaigns.id}`, {
        campaigns: selectedCampaigns
      });
      toast.success('Kampagnenzuordnung gespeichert');
      setCampaignDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { name: formData.name, role: formData.role };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await put(`/users/${editingUser.id}`, updateData);
        toast.success('Benutzer aktualisiert');
      } else {
        await post('/users', formData);
        toast.success('Benutzer erstellt');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?')) return;
    try {
      await del(`/users/${userId}`);
      toast.success('Benutzer gelöscht');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const getCampaignNames = (campaignIds) => {
    if (!campaignIds || campaignIds.length === 0) return [];
    return campaignIds.map(id => {
      const campaign = campaigns.find(c => c.id === id);
      return campaign ? campaign.name : id;
    });
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Benutzerverwaltung</h1>
          <p className="text-gray-500 mt-1">
            Verwalten Sie die Benutzer und deren Kampagnenzuordnungen
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#E63946] hover:bg-[#D62839]" data-testid="create-user-button">
              <Plus className="h-4 w-4 mr-2" />
              Neuer Benutzer
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}</DialogTitle>
              <DialogDescription>
                {editingUser ? 'Aktualisieren Sie die Benutzerdaten' : 'Erstellen Sie einen neuen Benutzer'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-form">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="user-name-input"
                />
              </div>
              {!editingUser && (
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="user-email-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="password">
                  {editingUser ? 'Neues Passwort (leer lassen um nicht zu ändern)' : 'Passwort *'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Rolle</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call_agent">Call Agent</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" className="bg-[#E63946] hover:bg-[#D62839]" data-testid="user-submit-button">
                  {editingUser ? 'Aktualisieren' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card data-testid="users-table-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5" />
            Benutzer
            <Badge variant="secondary" className="ml-2">
              {users.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>E-Mail</th>
                  <th>Rolle</th>
                  <th>Kampagnen</th>
                  <th>Erstellt am</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} data-testid={`user-row-${user.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          user.role === 'admin' ? 'bg-[#E63946]' : 'bg-gray-400'
                        }`}>
                          {user.role === 'admin' ? (
                            <Shield className="h-4 w-4 text-white" />
                          ) : (
                            <User className="h-4 w-4 text-white" />
                          )}
                        </div>
                        <span className="font-medium">{user.name}</span>
                      </div>
                    </td>
                    <td className="text-gray-600">{user.email}</td>
                    <td>
                      <Badge className={
                        user.role === 'admin' 
                          ? 'bg-[#E63946] text-white' 
                          : 'bg-gray-100 text-gray-800'
                      }>
                        {user.role === 'admin' ? 'Admin' : 'Call Agent'}
                      </Badge>
                    </td>
                    <td>
                      {user.role === 'admin' ? (
                        <span className="text-gray-400 text-sm">Alle Kampagnen</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {getCampaignNames(user.campaigns).length > 0 ? (
                            getCampaignNames(user.campaigns).map((name, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {name}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-gray-400 text-sm">Keine Kampagnen</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="text-gray-600">{formatDate(user.created_at)}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        {user.role !== 'admin' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openCampaignDialog(user)}
                            title="Kampagnen zuordnen"
                            data-testid={`assign-campaigns-${user.id}`}
                          >
                            <FolderKanban className="h-4 w-4 text-[#E63946]" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(user)}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(user.id)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Campaign Assignment Dialog */}
      <Dialog open={campaignDialogOpen} onOpenChange={setCampaignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kampagnen zuordnen</DialogTitle>
            <DialogDescription>
              Wählen Sie die Kampagnen, auf die {selectedUserForCampaigns?.name} Zugriff haben soll
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[300px] overflow-y-auto">
            {campaigns.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Keine Kampagnen vorhanden</p>
            ) : (
              campaigns.map((campaign) => (
                <div key={campaign.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                  <Checkbox
                    id={`campaign-${campaign.id}`}
                    checked={selectedCampaigns.includes(campaign.id)}
                    onCheckedChange={() => handleCampaignToggle(campaign.id)}
                    data-testid={`campaign-checkbox-${campaign.id}`}
                  />
                  <label
                    htmlFor={`campaign-${campaign.id}`}
                    className="flex-1 cursor-pointer"
                  >
                    <span className="font-medium">{campaign.name}</span>
                    {campaign.beschreibung && (
                      <p className="text-sm text-gray-500">{campaign.beschreibung}</p>
                    )}
                    <span className="text-xs text-gray-400">{campaign.lead_count} Leads</span>
                  </label>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCampaignDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleSaveCampaigns}
              className="bg-[#E63946] hover:bg-[#D62839]"
              data-testid="save-campaigns-button"
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
